import {Injectable} from '@angular/core';
import {AuthService} from '../../auth/auth.service';
import {JwtHelperService} from '@auth0/angular-jwt';
import {AppConfig} from "../types/appConfig.types";
import localeDataDe from '@angular/common/locales/de-AT';
import localeDataEnMy from '@angular/common/locales/en-MY';
import localeDataUk from '@angular/common/locales/uk';
import localeDataBs from '@angular/common/locales/bs';
import localeDataBg from '@angular/common/locales/bg';
import localeDataHr from '@angular/common/locales/hr';
import localeDataHu from '@angular/common/locales/hu';
import localeDataRo from '@angular/common/locales/ro';
import localeDataSr from '@angular/common/locales/sr';
import localeDataSk from '@angular/common/locales/sk';
import localeDataSl from '@angular/common/locales/sl';
import localeDataPt from '@angular/common/locales/pt';
import localeDataSv from '@angular/common/locales/sv';
import localeDataMk from '@angular/common/locales/mk';
import {deepMerge} from "../../utils/deep-merge";

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private jwtHelper = new JwtHelperService();
  private static appConfig: AppConfig = {
    carhubApplications: [],
    country: "",
    datadogConfig: undefined as any,
    datadogTeam: "",
    features: undefined as any,
    jwtWhiteListedDomains: [],
    localeId: "",
    loginUrl: "",
    tdsBaseUrl: "",
    tdsLanguage: "",
    tdsMarket: "",
    fwBaseUrl: "",
    stage: "",
  };

  async loadAppConfig(): Promise<void> {
    const token = AuthService.getRawToken();
    const headers: HeadersInit = token
      ? { Authorization: `Bearer ${token}` }
      : {};

    let stage = 'dev';
    try {
      const stageResponse = await fetch(
        `${document.location.origin}/api/stage`,
        {
          headers,
        },
      );
      if (stageResponse.ok) {
        stage = await stageResponse.text();
      }
    } catch {}

    const country = this.jwtHelper.decodeToken(token)?.country || 'at';

    const [baseConfigResponse, localeConfigResponse] = await Promise.all([
      fetch(`config/_base.${stage}.json`),
      fetch(`config/${country.toLowerCase()}.${stage}.json`),
    ]);

    const baseConfig = (await baseConfigResponse.json()) as AppConfig;
    const localeConfig = (await localeConfigResponse.json()) as AppConfig;

    ConfigService.appConfig = this.mergeConfiguration(baseConfig, localeConfig);
  }

  static getConfig(): AppConfig {
    if (!ConfigService.appConfig) {
      throw new Error('Configuration not loaded!');
    }
    return ConfigService.appConfig;
  }

  public getLocaleData() {
    const country = ConfigService.getConfig().country.toLowerCase();
    switch (country) {
      case 'bg':
        return localeDataBg;
      case 'ba':
        return localeDataBs;
      case 'hr':
        return localeDataHr;
      case 'hu':
        return localeDataHu;
      case 'mk':
        return localeDataMk;
      case 'my':
        return localeDataEnMy;
      case 'pt':
        return localeDataPt;
      case 'ro':
        return localeDataRo;
      case 'sk':
        return localeDataSk;
      case 'si':
        return localeDataSl;
      case 'rs':
        return localeDataSr;
      case 'se':
        return localeDataSv;
      case 'ua':
        return localeDataUk;
      default:
        return localeDataDe;
    }
  }

  /**
   * If a locale configurations is available, merges the given configuration (e.g. at-qa.json) into the base configuration (qa.json).
   * If the locale config isn't given, returns the base config.
   * @param baseConfig the base configuration to merge the given configuration into (e.g. qa.json)
   * @param localeConfig (optional) the configuration to merge into the default configuration (e.g. at-qa.json)
   */
  public mergeConfiguration(baseConfig: AppConfig, localeConfig?: AppConfig): AppConfig {
    return localeConfig ? deepMerge(baseConfig, localeConfig) : baseConfig;
  }
}
